import React, { FC, useState, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react';
import Button from '@app/components/Button';
import useAppStores from '@app/hooks/useAppStores';
import useInfraStores from '@app/hooks/useInfraStores';
import MorteeMode from '@mortee/morteeMode';
import {
  Container,
  MiddleScroll,
  PayeeDetailsSection,
  SectionHeader,
  AddAccountButtonContainer,
  BottomActions,
  BottomActionsRow,
  SaveButton,
  RoleToggleRow,
  RoleToggleLabel,
  RoleToggleSwitch,
} from './NewVerificationTab.styles';
import {
  PAYEE_DETAILS_COLUMNS,
  ACCOUNT_DETAILS_COLUMNS,
  AccountSection,
  ExpandableTableRow,
  INITIAL_PAYEE_DETAILS_ROWS,
  INITIAL_ACCOUNT_DETAILS_ROWS,
} from './NewVerificationTab.data';
import {
  populatePayeeDetailsRows,
  populateAccountDetailsRows,
  payeeRowsHaveManualOrFinalReviewChanges,
  accountSectionsHaveManualOrFinalReviewChanges,
  payeeRowsHaveManualProcessChanges,
  accountSectionsHaveManualProcessChanges,
  payeeRowsHaveFinalReviewChanges,
  accountSectionsHaveFinalReviewChanges,
  accountSectionsHaveMetadataChanges,
  payeeRowsHaveColumnData,
  accountSectionsHaveColumnData,
  buildManualPayeeVerificationRequest,
  buildPayeeVerificationRequest,
  buildAllAccountRequests,
  mergeAdditionalBankCodeAccounts,
  fillAdditionalBankCodeRow,
  extractVendorValue,
  isEmptyValue,
} from './NewVerificationTab.utils';
import { GenericTable } from './components/GenericTable';
import { TopSection } from './components/TopSection';
import { AccountSection as AccountSectionComponent } from './components/AccountSection';
import { DeleteConfirmDialog } from './components/DeleteConfirmDialog';
import { DATA_POINT_ADD_LIMITS, ADDITIONAL_BANK_CODE_LABEL, NOT_RELEVANT } from './NewVerificationTab.constants';
import { useRowManagement } from './hooks/useRowManagement';
import {
  SupplierValidationRecord,
  SupplierValidationPayeeWithAccountsVerification,
  SupplierValidationVerificationRecordEvidence,
  VerificationsAndAutoMatch,
  VerificationFlowType,
  SupplierValidationRecordStatus,
  SupplierValidationRecordStatusSubsets,
  transformSupplierValidationRecord,
  StoreSupplierValidationPayeeVerificationRecordRequest,
  StoreSupplierValidationAccountVerificationRecordRequest,
} from '@mortee/domain/validationSystem';
import { SanctionsScreeningResultStatus } from '@app/domain/validatedPayee';
import validationSystemServices from '@mortee/services/validationSystemServices';
import { shoot } from '@app/utils/messageLauncher';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';
import { showCustomModalAsync, showYesNoModalAsync } from '@app/components/Modal';
import ModalAppContext from '@app/ModalAppContext';
import useModalContext from '@app/hooks/useModalContext';
import Toggle from '@app/components/inputs/Toggle';
import { readVerificationRole, writeVerificationRole, VerificationRole } from '@app/stores/verificationRoleCache';
import ConditionalTooltip from '@app/components/ConditionalTooltip';
import { nanoid } from 'nanoid';
import VerificationPayeeAndAccountsReviewAndCompleteModal from './reviewModals/VerificationReviewAndCompleteModal';
import VerificationReviewAndChangeToWaitingForReviewModal from './reviewModals/VerificationReviewAndChangeToWaitingForReviewModal';
import VerificationReviewWarningsModal from './reviewModals/VerificationReviewWarningsModal';

interface Props {
  className?: string;
  record: SupplierValidationRecord;
  verificationData: SupplierValidationPayeeWithAccountsVerification;
  evidence?: SupplierValidationVerificationRecordEvidence;
  verificationsAndAutoMatch: VerificationsAndAutoMatch;
  onIsFormUnsavedChanged?: (isUnsaved: boolean) => void;
  onSaved?: (
    newVerification: SupplierValidationPayeeWithAccountsVerification | undefined,
    updatedRecord: SupplierValidationRecord | undefined,
  ) => void;
}
const NewVerificationTab: FC<Props> = observer(
  ({ record, className, verificationData, evidence, verificationsAndAutoMatch, onIsFormUnsavedChanged, onSaved }) => {
    const { validatedPayeesManagementStore } = useAppStores<MorteeMode>();
    const { permissionsStore, userStore, navigationStore } = useInfraStores<MorteeMode>();
    const modalContext = useModalContext();
    const allLEITypesLoadable = validatedPayeesManagementStore.allLeiTypes;
    const taxIdTypes = allLEITypesLoadable.map(
      (loaded) => [...Object.values(loaded.strongTypes).filter(Boolean), ...loaded.regularTypes] as LegalEntityIdType[],
    );
    // Map of typeId -> country codes for which that type is configured as a Semi-Strong LEI. Passed to
    // the edit dialog so it can show a read-only indication for the selected type + country. Undefined
    // until the LEI types load.
    const semiStrongCountryCodesByTypeId = allLEITypesLoadable.resolve(
      (loaded) => loaded.semiStrongCountryCodesByTypeId,
      () => undefined,
    );

    // When the user holds BOTH validation permissions, an explicit Editor/Approver toggle (persisted in
    // localStorage) decides which flow they act on. Single-permission users keep the original derivation.
    const hasBothRoles = permissionsStore.isValidationEditor && permissionsStore.isValidationApprover;
    const [verificationRole] = useState<VerificationRole>(() => readVerificationRole() ?? 'approver');

    // Effective per-column edit permissions. With both roles, the selected role gates the editable column,
    // the top section, and the account-type/metadata routing (all key off these two flags + activeFlow).
    // Editor -> Manual only; Approver -> Final only. Otherwise fall back to the raw permissions.
    const canEditManualProcess = hasBothRoles
      ? verificationRole === 'editor'
      : permissionsStore.isValidationEditor || permissionsStore.isValidationApprover;
    const canEditFinalReview = hasBothRoles ? verificationRole === 'approver' : permissionsStore.isValidationApprover;

    // Read the Knox ID only from the verification payee's alreadyExistingValidatedPayee.knoxId
    // (Final, then Manual, then verificationData). It is deliberately NOT backed by record.knoxId:
    // after a verification is completed the record carries the resulting (newly created) Knox ID, so
    // falling back to record.knoxId prefilled that new id when the record was reopened. The field
    // must reflect only the "already existing" payee the operator pointed at, so the fallback was
    // dropped (see commit "Remove new KnoxID after completion").
    const initialKnoxId =
      verificationsAndAutoMatch.finalVerification.payee?.alreadyExistingValidatedPayee?.knoxId ??
      verificationsAndAutoMatch.manualVerification.payee?.alreadyExistingValidatedPayee?.knoxId ??
      verificationData.payee?.alreadyExistingValidatedPayee?.knoxId;

    const initialEffectiveDate = evidence?.effectiveDate || verificationData?.payee?.effectiveTimestamp || undefined;
    const initialPrivatePayee =
      verificationsAndAutoMatch.manualVerification.payee?.isPrivate ?? verificationData?.payee?.isPrivate ?? false;
    const initialSanctionsStatus =
      (verificationsAndAutoMatch.manualVerification.payee?.sanctionsScreening?.result as SanctionsScreeningResultStatus | null) ??
      (verificationData.payee?.sanctionsScreening?.result as SanctionsScreeningResultStatus | null) ??
      null;
    const initialSanctionsScreeningDate =
      verificationsAndAutoMatch.manualVerification.payee?.sanctionsScreening?.screeningTimestamp ??
      verificationData.payee?.sanctionsScreening?.screeningTimestamp ??
      undefined;

    const [knoxId, setKnoxId] = useState(initialKnoxId);
    const [effectiveDate, setEffectiveDate] = useState<number | undefined>(initialEffectiveDate);
    const [privatePayee, setPrivatePayee] = useState(initialPrivatePayee);
    const [sanctionsStatus, setSanctionsStatus] = useState<string | null>(initialSanctionsStatus);
    const [sanctionsScreeningDate, setSanctionsScreeningDate] = useState<number | undefined>(initialSanctionsScreeningDate);
    const [deleteAccountSectionConfirmOpen, setDeleteAccountSectionConfirmOpen] = useState(false);
    const [accountSectionToDelete, setAccountSectionToDelete] = useState<string | null>(null);

    // Populate payee details rows with data
    const payeeDetailsRows = useMemo(
      () =>
        populatePayeeDetailsRows(
          INITIAL_PAYEE_DETAILS_ROWS,
          record,
          verificationData,
          evidence,
          verificationsAndAutoMatch,
          record.enrichmentEvidence ?? [],
        ),
      [verificationData, evidence, verificationsAndAutoMatch, record],
    );

    // Populate account sections with data
    const accountSections = useMemo(() => {
      const accounts = verificationData.accounts || [];
      const manualAccounts = verificationsAndAutoMatch.manualVerification?.accounts ?? [];
      const finalAccounts = verificationsAndAutoMatch.finalVerification?.accounts ?? [];

      // Show the single empty account section only when the record has no accounts in ANY source.
      // verificationData (auto-match view) and the manual/final verification flows come from separate
      // endpoints; accounts can exist only in the Final flow (e.g. an approver-reviewed record) while
      // verificationData.accounts is empty. Keying the empty state on verificationData alone would
      // hide those accounts entirely, so fall through to the merge whenever any flow has accounts.
      if (accounts.length === 0 && manualAccounts.length === 0 && finalAccounts.length === 0) {
        return [
          {
            id: 'account-1',
            rows: populateAccountDetailsRows(
              INITIAL_ACCOUNT_DETAILS_ROWS,
              record,
              evidence,
              verificationsAndAutoMatch,
              0,
              record.enrichmentEvidence ?? [],
            ),
            validationType: undefined,
            privateAccount: false,
          },
        ];
      }

      const descriptors = mergeAdditionalBankCodeAccounts(manualAccounts, finalAccounts, record);

      return descriptors.map(({ primaryIndex, secondaryIndex }) => {
        // primaryIndex comes from the verification arrays, which can have more entries than
        // verificationData.accounts (they are sourced from different endpoints). Guard against an
        // out-of-bounds lookup so a misaligned response degrades instead of crashing the tab.
        const account = accounts[primaryIndex] as (typeof accounts)[number] | undefined;
        const rows = populateAccountDetailsRows(
          INITIAL_ACCOUNT_DETAILS_ROWS,
          record,
          evidence,
          verificationsAndAutoMatch,
          primaryIndex,
          record.enrichmentEvidence ?? [],
        );

        // When a secondary account was merged in, fill the "Additional Bank Code" row with its
        // bankCode (see fillAdditionalBankCodeRow — reuses any row applyAbaRoutingVendor created for
        // the ABA vendor value, and carries the secondary account's persisted change flag through).
        if (secondaryIndex !== undefined) {
          fillAdditionalBankCodeRow(
            rows,
            manualAccounts[secondaryIndex],
            finalAccounts[secondaryIndex],
            verificationsAndAutoMatch.autoMatchResult?.autoMatchDataSet,
          );
        }

        // Prefer the verification account (manual, then final) for display metadata: verificationData
        // may not contain this account when it exists only in a verification flow, in which case
        // `account` is undefined and its fields would wrongly fall back to defaults.
        const primaryVerificationAccount = manualAccounts[primaryIndex] ?? finalAccounts[primaryIndex];

        return {
          id: `account-${account?.staticId || primaryVerificationAccount?.staticId || primaryIndex}`,
          rows,
          validationType: account?.type || primaryVerificationAccount?.type || undefined,
          privateAccount: account?.isPrivate ?? primaryVerificationAccount?.isPrivate ?? false,
          manualVerificationStaticId: manualAccounts[primaryIndex]?.staticId,
          finalVerificationStaticId: finalAccounts[primaryIndex]?.staticId,
          secondaryManualVerificationStaticId:
            secondaryIndex !== undefined ? manualAccounts[secondaryIndex]?.staticId : undefined,
          secondaryFinalVerificationStaticId: secondaryIndex !== undefined ? finalAccounts[secondaryIndex]?.staticId : undefined,
        };
      });
    }, [verificationData, evidence, verificationsAndAutoMatch, record]);

    const [payeeDetailsRowsState, setPayeeDetailsRows] = useState<ExpandableTableRow[]>(payeeDetailsRows);
    const [accountSectionsState, setAccountSections] = useState<AccountSection[]>(accountSections);

    // Baselines that `hasChanges` compares the live state against. They start from the
    // props-derived values, are re-synced whenever the server data changes (re-fetch), and are
    // re-pointed to the just-saved state after a successful save so the form is no longer
    // considered dirty once the changes are persisted.
    const [baselinePayeeRows, setBaselinePayeeRows] = useState<ExpandableTableRow[]>(payeeDetailsRows);
    const [baselineAccountSections, setBaselineAccountSections] = useState<AccountSection[]>(accountSections);
    const [baselineTopSection, setBaselineTopSection] = useState<{
      knoxId: string | null;
      effectiveDate: number | undefined;
      privatePayee: boolean;
      sanctionsStatus: string | null;
      sanctionsScreeningDate: number | undefined;
    }>({
      knoxId: initialKnoxId,
      effectiveDate: initialEffectiveDate,
      privatePayee: initialPrivatePayee,
      sanctionsStatus: initialSanctionsStatus,
      sanctionsScreeningDate: initialSanctionsScreeningDate,
    });

    // Update state (and baseline) when server data changes
    useEffect(() => {
      setPayeeDetailsRows(payeeDetailsRows);
      setBaselinePayeeRows(payeeDetailsRows);
    }, [payeeDetailsRows]);

    useEffect(() => {
      setAccountSections(accountSections);
      setBaselineAccountSections(accountSections);
    }, [accountSections]);

    useEffect(() => {
      setBaselineTopSection({
        knoxId: initialKnoxId,
        effectiveDate: initialEffectiveDate,
        privatePayee: initialPrivatePayee,
        sanctionsStatus: initialSanctionsStatus,
        sanctionsScreeningDate: initialSanctionsScreeningDate,
      });
    }, [initialKnoxId, initialEffectiveDate, initialPrivatePayee, initialSanctionsStatus, initialSanctionsScreeningDate]);

    const topSectionHasChanges = useMemo(
      () =>
        knoxId !== baselineTopSection.knoxId ||
        effectiveDate !== baselineTopSection.effectiveDate ||
        privatePayee !== baselineTopSection.privatePayee ||
        sanctionsStatus !== baselineTopSection.sanctionsStatus ||
        sanctionsScreeningDate !== baselineTopSection.sanctionsScreeningDate,
      [knoxId, effectiveDate, privatePayee, sanctionsStatus, sanctionsScreeningDate, baselineTopSection],
    );

    // Check if there are any changes that should enable Save
    const hasChanges = useMemo(() => {
      const payeeHasChanges = payeeRowsHaveManualOrFinalReviewChanges(payeeDetailsRowsState, baselinePayeeRows);
      const accountsHaveChanges = accountSectionsHaveManualOrFinalReviewChanges(accountSectionsState, baselineAccountSections);
      const accountsMetadataChanged = accountSectionsHaveMetadataChanges(accountSectionsState, baselineAccountSections);
      return payeeHasChanges || accountsHaveChanges || accountsMetadataChanged || topSectionHasChanges;
    }, [payeeDetailsRowsState, baselinePayeeRows, accountSectionsState, baselineAccountSections, topSectionHasChanges]);

    useEffect(() => {
      onIsFormUnsavedChanged?.(hasChanges);
    }, [hasChanges, onIsFormUnsavedChanged]);

    const { handleAddSubRow: handlePayeeAddSubRow, handleDeleteRow: handlePayeeDeleteRow } = useRowManagement({
      rows: payeeDetailsRowsState,
      setRows: setPayeeDetailsRows,
    });

    const [isSaving, setIsSaving] = useState(false);

    // Whether each flow already has a stored payee. Seeded from the server props and flipped to true
    // after a successful POST so a subsequent save PATCHes instead of POSTing again. Without this, a
    // record that started with no payee would POST on every save (the props don't refresh between
    // saves), mirroring the staticId tracking the account save path already does.
    const [manualPayeeStored, setManualPayeeStored] = useState(!!verificationsAndAutoMatch.manualVerification?.payee);
    const [finalPayeeStored, setFinalPayeeStored] = useState(!!verificationsAndAutoMatch.finalVerification?.payee);

    // Re-sync from props when the server data changes (re-fetch), same as the row/baseline effects.
    useEffect(() => {
      setManualPayeeStored(!!verificationsAndAutoMatch.manualVerification?.payee);
      setFinalPayeeStored(!!verificationsAndAutoMatch.finalVerification?.payee);
    }, [verificationsAndAutoMatch.manualVerification?.payee, verificationsAndAutoMatch.finalVerification?.payee]);

    const handleSave = async (): Promise<void> => {
      if (!hasChanges || isSaving) return;
      setIsSaving(true);

      // Must contain every type the edit dialog offers (strong + regular), so parseLegalIdentifierString
      // can resolve the typeId of whichever type the user selected. Using only strongTypes.TaxId here
      // left every other selected type unresolvable, saving the identifier with typeId/typeName null.
      const allLeiTypes: LegalEntityIdType[] = allLEITypesLoadable.resolve(
        (loaded) => [...Object.values(loaded.strongTypes).filter(Boolean), ...loaded.regularTypes] as LegalEntityIdType[],
        () => [],
      );

      const topSectionHasChanges =
        knoxId !== baselineTopSection.knoxId ||
        effectiveDate !== baselineTopSection.effectiveDate ||
        privatePayee !== baselineTopSection.privatePayee ||
        sanctionsStatus !== baselineTopSection.sanctionsStatus ||
        sanctionsScreeningDate !== baselineTopSection.sanctionsScreeningDate;

      // Top-section fields (Knox ID, effective date, private, sanctions) are not review-column values,
      // so route a change to them to the flow the current user actually edits: Final when the user has
      // final-review (approver) permission, otherwise Manual. This mirrors how the account metadata and
      // the review columns are already scoped by permission, instead of always attributing the change to
      // Manual regardless of who edited it.
      const topSectionToFinal = topSectionHasChanges && activeFlow?.flowType === VerificationFlowType.Final;
      const topSectionToManual = topSectionHasChanges && activeFlow?.flowType === VerificationFlowType.Manual;

      const payeeHasManualChanges =
        payeeRowsHaveManualProcessChanges(payeeDetailsRowsState, baselinePayeeRows) || topSectionToManual;
      const payeeHasFinalReviewEdits =
        payeeRowsHaveFinalReviewChanges(payeeDetailsRowsState, baselinePayeeRows) || topSectionToFinal;

      // validationType / privateAccount live on the section (not a review column). They are written to
      // whichever request(s) get saved, so a change to them alone must still trigger a save — routed to
      // the flow the current user actually edits (Final for an approver, else Manual). Routing to the
      // active flow (rather than blindly both) avoids POSTing a spurious Final record for a manual-only
      // editor, matching how a column edit is already scoped by the editable-column permission.
      const accountsMetadataChanged = accountSectionsHaveMetadataChanges(accountSectionsState, baselineAccountSections);
      const metadataToManual = accountsMetadataChanged && activeFlow?.flowType === VerificationFlowType.Manual;
      const metadataToFinal = accountsMetadataChanged && activeFlow?.flowType === VerificationFlowType.Final;
      const accountsHaveManualChanges =
        accountSectionsHaveManualProcessChanges(accountSectionsState, baselineAccountSections) || metadataToManual;
      const accountsHaveFinalReviewEdits =
        accountSectionsHaveFinalReviewChanges(accountSectionsState, baselineAccountSections) || metadataToFinal;
      const accountsHaveChanges = accountsHaveManualChanges || accountsHaveFinalReviewEdits;

      const savePayeeRecord = async (
        request: StoreSupplierValidationPayeeVerificationRecordRequest,
        flowType: VerificationFlowType,
      ): Promise<void> => {
        const hasStoredPayee = flowType === 'Final' ? finalPayeeStored : manualPayeeStored;

        if (hasStoredPayee) {
          const updateRequest = Object.fromEntries(
            Object.entries(request).map(([key, value]) => [key, { content: value }]),
          ) as UpdateRequestObject<StoreSupplierValidationPayeeVerificationRecordRequest>;
          await validationSystemServices.updatePayeeVerificationRecordV2(record.staticId, updateRequest, flowType);
          return;
        }
        await validationSystemServices.storeSupplierValidationPayeeVerificationRecordV2(record.staticId, request, flowType);
        // The payee now exists for this flow — remember it so a later save (this session, before the
        // props refresh) PATCHes instead of POSTing again.
        if (flowType === 'Final') {
          setFinalPayeeStored(true);
        } else {
          setManualPayeeStored(true);
        }
      };

      const saveAccountRecord = async (
        request: StoreSupplierValidationAccountVerificationRecordRequest,
        flowType: VerificationFlowType,
        storedAccountStaticId: string | undefined,
      ): Promise<string | undefined> => {
        if (storedAccountStaticId) {
          const updateRequest = Object.fromEntries(
            Object.entries(request).map(([key, value]) => [key, { content: value }]),
          ) as UpdateRequestObject<StoreSupplierValidationAccountVerificationRecordRequest>;
          await validationSystemServices.updateAccountVerificationRecordV2(
            record.staticId,
            storedAccountStaticId,
            updateRequest,
            flowType,
          );
          return storedAccountStaticId;
        }
        const created = await validationSystemServices.storeSupplierValidationAccountVerificationRecordV2(
          record.staticId,
          request,
          flowType,
        );
        // The v2 create response wraps fields as FieldValue<...>; unwrap the staticId string so it can
        // be reused as the path param on a later PATCH (otherwise the object stringifies to
        // "[object Object]" in the URL).
        return created.staticId.value;
      };

      try {
        if (payeeHasManualChanges) {
          const manualPayeeRequest = buildManualPayeeVerificationRequest(
            payeeDetailsRowsState,
            knoxId ?? '',
            privatePayee,
            effectiveDate,
            sanctionsStatus,
            sanctionsScreeningDate,
            allLeiTypes,
          );
          await savePayeeRecord(manualPayeeRequest, VerificationFlowType.Manual);
        }

        if (payeeHasFinalReviewEdits) {
          const finalPayeeRequest = buildPayeeVerificationRequest(
            payeeDetailsRowsState,
            knoxId ?? '',
            privatePayee,
            effectiveDate,
            sanctionsStatus,
            sanctionsScreeningDate,
            allLeiTypes,
          );
          await savePayeeRecord(finalPayeeRequest, VerificationFlowType.Final);
        }

        if (accountsHaveChanges) {
          const groups = buildAllAccountRequests(accountSectionsState);
          // Map sectionId -> updated staticIds captured from POST responses.
          const sectionUpdates = new Map<
            string,
            Partial<
              Pick<
                AccountSection,
                | 'manualVerificationStaticId'
                | 'finalVerificationStaticId'
                | 'secondaryManualVerificationStaticId'
                | 'secondaryFinalVerificationStaticId'
              >
            >
          >();

          for (const group of groups) {
            const section = accountSectionsState.find((s) => s.id === group.sectionId);
            if (!section) continue;
            const manualId =
              group.slot === 'primary' ? section.manualVerificationStaticId : section.secondaryManualVerificationStaticId;
            const finalId =
              group.slot === 'primary' ? section.finalVerificationStaticId : section.secondaryFinalVerificationStaticId;

            let newManualId = manualId;
            let newFinalId = finalId;
            if (accountsHaveManualChanges) {
              newManualId = await saveAccountRecord(group.manual, VerificationFlowType.Manual, manualId);
            }
            if (accountsHaveFinalReviewEdits) {
              newFinalId = await saveAccountRecord(group.final, VerificationFlowType.Final, finalId);
            }

            const prev = sectionUpdates.get(group.sectionId) ?? {};
            sectionUpdates.set(group.sectionId, {
              ...prev,
              ...(group.slot === 'primary'
                ? { manualVerificationStaticId: newManualId, finalVerificationStaticId: newFinalId }
                : { secondaryManualVerificationStaticId: newManualId, secondaryFinalVerificationStaticId: newFinalId }),
            });
          }

          // Delete secondary accounts whose Additional Bank Code row was removed: section has a
          // secondary staticId but no Additional Bank Code row remains.
          for (const section of accountSectionsState) {
            const stillHasAdditional = section.rows.some(
              (r) => r.dataPointType === 'bankCode' && r.dataPoint === ADDITIONAL_BANK_CODE_LABEL,
            );
            if (stillHasAdditional) continue;
            if (section.secondaryManualVerificationStaticId) {
              await validationSystemServices.deleteAccountVerificationRecordV2(
                record.staticId,
                section.secondaryManualVerificationStaticId,
                VerificationFlowType.Manual,
              );
            }
            if (section.secondaryFinalVerificationStaticId) {
              await validationSystemServices.deleteAccountVerificationRecordV2(
                record.staticId,
                section.secondaryFinalVerificationStaticId,
                VerificationFlowType.Final,
              );
            }
            if (section.secondaryManualVerificationStaticId || section.secondaryFinalVerificationStaticId) {
              const prev = sectionUpdates.get(section.id) ?? {};
              sectionUpdates.set(section.id, {
                ...prev,
                secondaryManualVerificationStaticId: undefined,
                secondaryFinalVerificationStaticId: undefined,
              });
            }
          }

          // Apply captured staticId updates so the next save PATCHes instead of POSTing.
          if (sectionUpdates.size > 0) {
            setAccountSections((prev) => prev.map((s) => (sectionUpdates.has(s.id) ? { ...s, ...sectionUpdates.get(s.id) } : s)));
          }
        }

        // Re-point the baselines to the state we just persisted, so the form is no longer
        // considered dirty (Save button disables, the unsaved-changes guard stops firing).
        setBaselinePayeeRows(payeeDetailsRowsState);
        setBaselineAccountSections(accountSectionsState);
        setBaselineTopSection({ knoxId, effectiveDate, privatePayee, sanctionsStatus, sanctionsScreeningDate });

        shoot({ type: 'success', closeable: true }, 'Verification saved successfully.');
      } catch {
        // Error notifications are handled by the service layer
      } finally {
        setIsSaving(false);
      }
    };

    const resetSanctions = (): void => {
      setSanctionsStatus(null);
      setSanctionsScreeningDate(undefined);
    };

    const handleAddAccountSection = (): void => {
      const newSection: AccountSection = {
        // nanoid (not Date.now) so two sections created in the same millisecond — e.g. add then
        // duplicate, or two quick adds — never collide. A duplicate id makes updateAccountSection's
        // map update BOTH sections and the save loop's find() target the wrong one, which mixed
        // account values together.
        id: `account-${nanoid()}`,
        rows: INITIAL_ACCOUNT_DETAILS_ROWS.map((row) => ({
          ...row,
          vendor: '',
          evidence: undefined,
          automatedMatching: null,
          manualProcess: '',
          finalReview: '',
        })),
        validationType: undefined,
        privateAccount: false,
      };
      setAccountSections((prev) => [...prev, newSection]);
    };

    const handleDeleteAccountSection = async (sectionId: string): Promise<void> => {
      const section = accountSectionsState.find((s) => s.id === sectionId);
      if (section) {
        try {
          if (section.manualVerificationStaticId) {
            await validationSystemServices.deleteAccountVerificationRecordV2(
              record.staticId,
              section.manualVerificationStaticId,
              VerificationFlowType.Manual,
            );
          }
          if (section.finalVerificationStaticId) {
            await validationSystemServices.deleteAccountVerificationRecordV2(
              record.staticId,
              section.finalVerificationStaticId,
              VerificationFlowType.Final,
            );
          }
          if (section.secondaryManualVerificationStaticId) {
            await validationSystemServices.deleteAccountVerificationRecordV2(
              record.staticId,
              section.secondaryManualVerificationStaticId,
              VerificationFlowType.Manual,
            );
          }
          if (section.secondaryFinalVerificationStaticId) {
            await validationSystemServices.deleteAccountVerificationRecordV2(
              record.staticId,
              section.secondaryFinalVerificationStaticId,
              VerificationFlowType.Final,
            );
          }
        } catch {
          shoot({ type: 'error', closeable: true }, 'Could not delete account verification record. Please try again.');
          return;
        }
      }
      setAccountSections((prev) => prev.filter((section) => section.id !== sectionId));
    };

    const handleDuplicateAccountSection = (sectionId: string): void => {
      setAccountSections((prev) => {
        const sectionToDuplicate = prev.find((section) => section.id === sectionId);
        if (!sectionToDuplicate) {
          return prev;
        }

        const sectionIndex = prev.findIndex((section) => section.id === sectionId);
        if (sectionIndex === -1) {
          return prev;
        }

        const duplicatedSection: AccountSection = {
          // Unique id (nanoid, not Date.now) — see handleAddAccountSection. A duplicate created in
          // the same millisecond as its source (or another add) would otherwise share an id and get
          // its edits/saves confused with the other section.
          id: `account-${nanoid()}`,
          rows: sectionToDuplicate.rows.map((row) => ({ ...row })),
          validationType: sectionToDuplicate.validationType,
          privateAccount: sectionToDuplicate.privateAccount,
        };

        const newSections = [...prev];
        newSections.splice(sectionIndex + 1, 0, duplicatedSection);
        return newSections;
      });
    };

    const updateAccountSection = (sectionId: string, updates: Partial<AccountSection>): void => {
      setAccountSections((prev) => prev.map((section) => (section.id === sectionId ? { ...section, ...updates } : section)));
    };

    const handleAccountSectionRowUpdate = (
      sectionId: string,
      rowIndex: number,
      columnKey: string,
      newValue: React.ReactNode,
    ): void => {
      const section = accountSectionsState.find((s) => s.id === sectionId);
      if (!section) return;

      updateAccountSection(sectionId, {
        rows: section.rows.map((row, idx) => (idx === rowIndex ? { ...row, [columnKey]: newValue } : row)),
      });
    };

    const handleAccountSectionAddSubRow = (sectionId: string, rowIndex: number): void => {
      const section = accountSectionsState.find((s) => s.id === sectionId);
      if (!section) return;

      const parentRow = section.rows[rowIndex];
      if (!parentRow.showAddIcon || !parentRow.dataPointType) {
        return;
      }

      const parentDataPoint = typeof parentRow.dataPoint === 'string' ? parentRow.dataPoint : String(parentRow.dataPoint || '');
      const baseName = parentDataPoint.replace(/\s+\d+$/, '').trim();

      const limit = DATA_POINT_ADD_LIMITS[parentRow.dataPointType] ?? Infinity;

      let childRowCount = 0;
      let insertIndex = rowIndex + 1;

      for (let i = rowIndex + 1; i < section.rows.length; i++) {
        const currentRow = section.rows[i];
        if (currentRow.isChildRow && currentRow.dataPointType === parentRow.dataPointType) {
          childRowCount++;
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      if (childRowCount >= limit) {
        return;
      }

      const newDataPoint =
        parentRow.dataPointType === 'bankCode' && childRowCount === 0
          ? ADDITIONAL_BANK_CODE_LABEL
          : `${baseName} ${childRowCount + 2}`;

      const newRow: ExpandableTableRow = {
        dataPoint: newDataPoint,
        dataPointType: parentRow.dataPointType,
        vendor: '-',
        evidence: undefined,
        // A newly-added row has not been matched against anything yet; 'Unknown' reflects
        // that no match has been determined (mirrors useRowManagement for payee sub-rows).
        automatedMatching: 'Unknown',
        manualProcess: '',
        finalReview: '',
        showDeleteIcon: true,
        isChildRow: true,
      };

      const newRows = [...section.rows];
      newRows.splice(insertIndex, 0, newRow);
      updateAccountSection(sectionId, { rows: newRows });
    };

    const handleAccountSectionDeleteRow = (sectionId: string, rowIndex: number): void => {
      const section = accountSectionsState.find((s) => s.id === sectionId);
      if (!section) return;

      const row = section.rows[rowIndex];

      // Deleting the "Additional Bank Code" row when it still carries a Vendor Registration value must
      // NOT drop the row (which would delete the secondary account on save and lose the vendor's ABA
      // reference). Instead mark it "Not relevant" in the column the current user actually edits —
      // Manual Processing for a Validator/editor, Final Review for an Approver — mirroring how every
      // other edit is scoped to activeFlow. NOT_RELEVANT serializes back to null on save (readBankCode),
      // so this records an explicit "not relevant" for that flow rather than removing the row.
      const isAdditionalBankCodeRow = row?.dataPointType === 'bankCode' && row?.dataPoint === ADDITIONAL_BANK_CODE_LABEL;
      const vendorHasValue = row ? !isEmptyValue(extractVendorValue(row)) : false;
      if (isAdditionalBankCodeRow && vendorHasValue && activeFlow) {
        const newRows = section.rows.map((r, idx) => (idx === rowIndex ? { ...r, [activeFlow.columnKey]: NOT_RELEVANT } : r));
        updateAccountSection(sectionId, { rows: newRows });
        return;
      }

      const newRows = [...section.rows];
      newRows.splice(rowIndex, 1);
      updateAccountSection(sectionId, { rows: newRows });
    };

    const handlePrivatePayeeChange = (value: boolean): void => {
      setPrivatePayee(value);
      setAccountSections((prev) => prev.map((section) => ({ ...section, privateAccount: value })));
    };

    const handleRoleToggle = async (newRole: VerificationRole): Promise<void> => {
      if (newRole === verificationRole) return;

      // A role switch reloads the page to re-initialize every flow-derived baseline cleanly. If there are
      // unsaved edits, confirm first because the reload discards them; otherwise reload silently.
      if (hasChanges) {
        const confirmed = await showYesNoModalAsync(
          'Switching role will reload the page and discard your unsaved changes. Continue?',
          'Switch role',
          'Switch',
          'Cancel',
        );
        if (!confirmed) return;
      }

      writeVerificationRole(newRole);
      // Established reload pattern (see InfraStores.handleSessionExpired).
      window.location.reload();
    };

    const formHasSavedData = !!(verificationData?.payee || verificationData?.accounts?.length);

    // The verification column the current user acts on. Final wins when both permissions apply.
    const activeFlow = useMemo<{ flowType: VerificationFlowType; columnKey: 'manualProcess' | 'finalReview' } | null>(() => {
      if (canEditFinalReview) return { flowType: VerificationFlowType.Final, columnKey: 'finalReview' };
      if (canEditManualProcess) return { flowType: VerificationFlowType.Manual, columnKey: 'manualProcess' };
      return null;
    }, [canEditFinalReview, canEditManualProcess]);

    const checkWarningsDisabled = useMemo(() => {
      if (!activeFlow) return true;

      const columnHasChanges =
        activeFlow.columnKey === 'finalReview'
          ? payeeRowsHaveFinalReviewChanges(payeeDetailsRowsState, baselinePayeeRows) ||
            accountSectionsHaveFinalReviewChanges(accountSectionsState, baselineAccountSections)
          : payeeRowsHaveManualProcessChanges(payeeDetailsRowsState, baselinePayeeRows) ||
            accountSectionsHaveManualProcessChanges(accountSectionsState, baselineAccountSections);

      // "Saved data" is measured against the baseline (last saved) rows, not the live edits.
      const columnHasSavedData =
        payeeRowsHaveColumnData(baselinePayeeRows, activeFlow.columnKey) ||
        accountSectionsHaveColumnData(baselineAccountSections, activeFlow.columnKey);

      return topSectionHasChanges || columnHasChanges || !columnHasSavedData;
    }, [
      activeFlow,
      topSectionHasChanges,
      payeeDetailsRowsState,
      baselinePayeeRows,
      accountSectionsState,
      baselineAccountSections,
    ]);

    const handleCheckWarningsClick = async (): Promise<void> => {
      if (!activeFlow) return;
      const payeeWithAccountsWarnings = await validatedPayeesManagementStore.calcPayeeAndAccountsCreationAlertsV2(
        record.staticId,
        activeFlow.flowType,
        true,
      );
      await showCustomModalAsync(
        (onDone) => (
          <ModalAppContext {...modalContext}>
            <VerificationReviewWarningsModal
              payeeWithAccountsWarnings={payeeWithAccountsWarnings}
              supplierValidationRecord={record}
              semiStrongCountryCodesByTypeId={semiStrongCountryCodesByTypeId}
              onClose={onDone}
            />
          </ModalAppContext>
        ),
        { maskClosable: true },
      );
    };

    const handleWaitingForApprovalClick = async (): Promise<void> => {
      if (!activeFlow) return;
      const payeeWithAccountsWarnings = await validatedPayeesManagementStore.calcPayeeAndAccountsCreationAlertsV2(
        record.staticId,
        activeFlow.flowType,
        true,
      );
      const shouldSave = await showCustomModalAsync(
        (onAccept, onDecline) => (
          <ModalAppContext {...modalContext}>
            <VerificationReviewAndChangeToWaitingForReviewModal
              payeeWithAccountsWarnings={payeeWithAccountsWarnings}
              supplierValidationRecord={record}
              onConfirm={onAccept}
              onClose={onDecline}
            />
          </ModalAppContext>
        ),
        { maskClosable: true },
      );
      if (!shouldSave) return;

      const messageKey = `${record.staticId}-waiting-for-approval`;
      const editPath = navigationStore.generateEditValidationRecordHref(record.staticId);
      const loadingMessage = shoot(
        { type: 'loading', key: messageKey, duration: null },
        <div>
          <a href={editPath} target='_blank' rel='noreferrer'>
            {record.presentationId}
          </a>{' '}
          - Changing status to &quot;Waiting for approval&quot;
        </div>,
      );
      try {
        const response = await validationSystemServices.updateValidationRecord(record.staticId, {
          status: { content: { value: SupplierValidationRecordStatus.waitingForApproval, reasonIds: null } },
        });
        const updatedRecord = transformSupplierValidationRecord(response.record, userStore.user?.id);
        onSaved?.(undefined, updatedRecord);
        shoot(
          { type: 'success', closeable: true, key: messageKey },
          <div>
            <a href={editPath} target='_blank' rel='noreferrer'>
              {record.presentationId}
            </a>{' '}
            - Updated
          </div>,
        );
      } catch {
        loadingMessage();
      }
    };

    const handleCompleteClick = async (): Promise<void> => {
      const payeeWithAccountsWarnings = await validatedPayeesManagementStore.calcPayeeAndAccountsCreationAlertsV2(
        record.staticId,
        VerificationFlowType.Final,
        true,
      );
      const shouldSave = await showCustomModalAsync(
        (onAccept, onDecline) => (
          <ModalAppContext {...modalContext}>
            <VerificationPayeeAndAccountsReviewAndCompleteModal
              payeeWithAccountsWarnings={payeeWithAccountsWarnings}
              supplierValidationRecord={record}
              onConfirm={onAccept}
              onClose={onDecline}
            />
          </ModalAppContext>
        ),
        { maskClosable: true },
      );
      if (!shouldSave) return;

      const messageKey = `${record.staticId}-complete-verification`;
      const editPath = navigationStore.generateEditValidationRecordHref(record.staticId);
      const loadingMessage = shoot(
        { type: 'loading', key: messageKey, duration: null },
        <div>
          <a href={editPath} target='_blank' rel='noreferrer'>
            Verification of {record.presentationId}
          </a>{' '}
          - Completing
        </div>,
      );
      try {
        await validationSystemServices.completeSupplierValidationVerificationRecord(
          record.staticId,
          record.presentationId,
          editPath,
        );
        onSaved?.(undefined, undefined);
        shoot(
          { type: 'success', closeable: true, key: messageKey },
          <div>
            <a href={editPath} target='_blank' rel='noreferrer'>
              Verification of {record.presentationId}
            </a>{' '}
            - Completed!
          </div>,
        );
      } catch {
        loadingMessage();
      }
    };

    return (
      <Container className={className}>
        {hasBothRoles && (
          <RoleToggleRow>
            <RoleToggleLabel data-active={verificationRole === 'editor'}>Validator</RoleToggleLabel>
            <RoleToggleSwitch>
              <Toggle
                accessibilityLabel='Select whether you are working as validator or approver'
                name='verification-role'
                value={verificationRole === 'approver'}
                onChange={(value): Promise<void> => handleRoleToggle(value ? 'approver' : 'editor')}
              />
            </RoleToggleSwitch>
            <RoleToggleLabel data-active={verificationRole === 'approver'}>Approver</RoleToggleLabel>
          </RoleToggleRow>
        )}
        <MiddleScroll>
          {/* TopSection lives inside the scroll area so it scrolls away with the content; only the
              role toggle above stays pinned. */}
          <TopSection
            knoxId={knoxId || ''}
            effectiveDate={effectiveDate}
            verificationData={verificationData}
            evidence={evidence || undefined}
            privatePayee={privatePayee}
            sanctionsStatus={sanctionsStatus}
            sanctionsScreeningDate={sanctionsScreeningDate}
            onKnoxIdChange={setKnoxId}
            onEffectiveDateChange={setEffectiveDate}
            onPrivatePayeeChange={handlePrivatePayeeChange}
            onSanctionsStatusChange={setSanctionsStatus}
            onSanctionsScreeningDateChange={setSanctionsScreeningDate}
            onResetSanctions={resetSanctions}
            hasChanges={hasChanges}
            isSaving={isSaving}
            onSave={handleSave}
          />
          <PayeeDetailsSection>
            <SectionHeader>Payee Details</SectionHeader>
            <GenericTable
              columns={PAYEE_DETAILS_COLUMNS}
              rows={payeeDetailsRowsState}
              taxIdTypes={taxIdTypes}
              semiStrongCountryCodesByTypeId={semiStrongCountryCodesByTypeId}
              canEditManualProcess={canEditManualProcess}
              canEditFinalReview={canEditFinalReview}
              onRowUpdate={(rowIndex, columnKey, newValue): void => {
                setPayeeDetailsRows((prevRows) => {
                  const updatedRows = [...prevRows];
                  if (updatedRows[rowIndex]) {
                    updatedRows[rowIndex] = {
                      ...updatedRows[rowIndex],
                      [columnKey]: newValue,
                    };
                  }
                  return updatedRows;
                });
              }}
              onAddSubRow={handlePayeeAddSubRow}
              onDeleteRow={handlePayeeDeleteRow}
            />
          </PayeeDetailsSection>

          {accountSectionsState.map((section, sectionIndex) => (
            <AccountSectionComponent
              key={section.id}
              section={section}
              sectionIndex={sectionIndex}
              totalSections={accountSectionsState.length}
              columns={ACCOUNT_DETAILS_COLUMNS}
              taxIdTypes={taxIdTypes}
              semiStrongCountryCodesByTypeId={semiStrongCountryCodesByTypeId}
              canEditManualProcess={canEditManualProcess}
              canEditFinalReview={canEditFinalReview}
              showOriginallyLyonsNote={record.doEvidencesContainsLyons && record.evidencesAmount > 1}
              onValidationTypeChange={(sectionId, value): void => {
                updateAccountSection(sectionId, { validationType: value });
              }}
              onPrivateAccountChange={(sectionId, value): void => {
                updateAccountSection(sectionId, { privateAccount: value });
              }}
              onDuplicate={handleDuplicateAccountSection}
              onDelete={(sectionId): void => {
                setAccountSectionToDelete(sectionId);
                setDeleteAccountSectionConfirmOpen(true);
              }}
              onRowUpdate={handleAccountSectionRowUpdate}
              onAddSubRow={handleAccountSectionAddSubRow}
              onDeleteRow={handleAccountSectionDeleteRow}
            />
          ))}

          <AddAccountButtonContainer>
            <Button id='add-new-account-btn' onClick={handleAddAccountSection}>
              + Add New Account
            </Button>
          </AddAccountButtonContainer>
        </MiddleScroll>

        <BottomActions>
          <BottomActionsRow>
            <SaveButton data-testid='btn-new-verification-save' disabled={!hasChanges || isSaving} onClick={handleSave}>
              {isSaving ? 'Saving…' : 'Save Changes'}
            </SaveButton>
          </BottomActionsRow>
          <BottomActionsRow>
            <Button
              id='btn-new-verification-check-warnings'
              appearance='text'
              disabled={checkWarningsDisabled}
              onClick={handleCheckWarningsClick}
            >
              CHECK WARNINGS
            </Button>
            {canEditManualProcess && SupplierValidationRecordStatusSubsets.preWaitingApproval.includes(record.status.value) && (
              <ConditionalTooltip
                showTooltip={record.isClosurePerformed}
                title='This verification has already been distributed'
                placement='top'
              >
                <Button
                  id='btn-new-verification-ready-for-review'
                  disabled={hasChanges || !formHasSavedData || record.isClosurePerformed}
                  onClick={handleWaitingForApprovalClick}
                >
                  READY FOR REVIEW
                </Button>
              </ConditionalTooltip>
            )}
            {canEditFinalReview && SupplierValidationRecordStatusSubsets.pendingApproval.includes(record.status.value) && (
              <ConditionalTooltip
                showTooltip={record.isClosurePerformed}
                title='This verification has already been distributed'
                placement='top'
              >
                <Button
                  id='btn-new-verification-complete'
                  disabled={hasChanges || !formHasSavedData || record.isClosurePerformed}
                  onClick={handleCompleteClick}
                >
                  COMPLETE
                </Button>
              </ConditionalTooltip>
            )}
          </BottomActionsRow>
        </BottomActions>

        <DeleteConfirmDialog
          isOpen={deleteAccountSectionConfirmOpen}
          onOpenChange={setDeleteAccountSectionConfirmOpen}
          onConfirm={async (): Promise<void> => {
            if (accountSectionToDelete) {
              await handleDeleteAccountSection(accountSectionToDelete);
            }
            setDeleteAccountSectionConfirmOpen(false);
            setAccountSectionToDelete(null);
          }}
          onCancel={(): void => {
            setDeleteAccountSectionConfirmOpen(false);
            setAccountSectionToDelete(null);
          }}
          type='accountSection'
          id='delete-account-section-confirm'
        />
      </Container>
    );
  },
);

export default NewVerificationTab;
